import api from '../api'

export const fetchEvaluationList = async ({
  page,
  employee,
  page_size,
  search,
  status
}: {
  page: number
  employee: string
  page_size: number
  search?: string
  status?: string
}) => {
  const params: Record<string, unknown> = {
    expand: 'employee,evaluator,job_profile',
    fields: '*,employee.id,employee.first_name,employee.last_name,employee.profile_picture,evaluator.first_name,evaluator.last_name,evaluator.profile_picture,job_profile',
    page_size,
    page,
    employee,
  }

  if (search) {
    params.search = search
  }

  if (status) {
    params.status = status
  }

  const response = await api.get('/evaluations/', {
    params,
  })
  return response.data
}

interface FetchEvaluationsParams {
  page: number
  page_size: number
  search?: string
  ordering?: Array<string> | string
  filters?: Record<string, string[]>
}

export type EvaluationStageKey = 'self' | 'evaluator_one' | 'evaluator_two' | 'report'

export interface EvaluationCycleStage {
  key: EvaluationStageKey
  completed: number
  total: number
  percent: number
}

export interface EvaluationCycleSummary {
  total: number
  cycle_start: string | null
  cycle_end: string | null
  stages: EvaluationCycleStage[]
}

export const fetchAllEmployeeEvaluations = async ({
  page,
  page_size,
  search,
  ordering,
  filters = {},
}: FetchEvaluationsParams) => {
  const params = new URLSearchParams()

  params.set('page', String(page))
  params.set('page_size', String(page_size))

  if (search) params.set('search', search)

  if (Array.isArray(ordering)) {
    ordering.forEach((o) => params.append('ordering', o))
  } else if (ordering) {
    params.set('ordering', ordering)
  }

  Object.entries(filters).forEach(([key, values]) => {
    values.forEach((value) => {
      params.append(key, value)
    })
  })

  const response = await api.get('/evaluations/latest-per-employee/', {
    params,
  })

  return response.data
}

export const fetchEvaluationCycleSummary = async (): Promise<EvaluationCycleSummary> => {
  const response = await api.get('/evaluations/cycle-summary/')
  return response.data
}

export const fetchEvaluation = async (evaluationId: string) => {
  const response = await api.get(`/evaluations/${evaluationId}/`, {
    params: {
      expand: 'job_profile',
      fields: '*,job_profile',
    },
  })
  return response.data
}

export const addEvaluation = async (employee: unknown) => {
  const response = await api.post('/evaluations/', employee)
  return response.data
}

export const updateEvaluation = async (evaluationId: string, evaluation: unknown) => {
  const response = await api.patch(`/evaluations/${evaluationId}/`, evaluation)
  return response.data
}

export const deleteEvaluation = async (evaluationId: string | number) => {
  const response = await api.delete(`/evaluations/${evaluationId}/`)
  return response.data
}

export const approveEvaluation = async (evaluationId: number) => {
  const response = await api.put(`/evaluations/${evaluationId}/approve/`)
  return response.data
}

export const publishEvaluation = async (evaluationId: string | number) => {
  const response = await api.put(`/evaluations/${evaluationId}/publish/`)
  return response.data
}

export const evaluationEmployeeComment = async (evaluationId: string, comment: unknown) => {
  const response = await api.put(`/evaluations/${evaluationId}/employee_comment/`, comment)
  return response.data
}

export const evaluationEvaluatorComment = async (evaluationId: string, comment: unknown) => {
  const response = await api.put(`/evaluations/${evaluationId}/evaluator_comment/`, comment)
  return response.data
}

export const unpublishEvaluation = async (evaluationId: string | number) => {
  const response = await api.put(`/evaluations/${evaluationId}/unpublish/`)
  return response.data
}

export interface EvaluationCycleHistory {
  id: string
  label: string
  duration: 'annual' | 'biannual' | 'quarterly' | 'custom'
  start_date: string
  end_date: string
  status: 'upcoming' | 'current' | 'completed'
}

export interface EvaluationCycleSettings {
  duration: EvaluationCycleHistory['duration']
  current_start: string | null
  current_end: string | null
  self_assessment_enabled: boolean
  self_survey_enabled: boolean
  evaluator_one_enabled: boolean
  evaluator_two_enabled: boolean
  conversation_report_enabled: boolean
  development_plan_enabled: boolean
  max_development_goals: 1 | 2 | 3
  history: EvaluationCycleHistory[]
}

export interface DevelopmentGoal {
  id: string
  evaluation: string
  title: string
  goal_type: 'competence' | 'skill' | 'task'
  timeline: string
  outcome: 'todo' | 'on_track' | 'partially_achieved' | 'achieved'
  plan: string
  due_date: string | null
  period: 'current' | 'previous'
}

export const fetchEvaluationCycleSettings = async (): Promise<EvaluationCycleSettings> => {
  const response = await api.get('/evaluations/cycle-settings/')
  return response.data
}

export const updateEvaluationCycleSettings = async (
  payload: Partial<EvaluationCycleSettings>,
): Promise<EvaluationCycleSettings> => {
  const response = await api.patch('/evaluations/cycle-settings/', payload)
  return response.data
}

export const fetchDevelopmentGoals = async (evaluationId: string): Promise<DevelopmentGoal[]> => {
  const response = await api.get(`/evaluations/${evaluationId}/development-goals/`)
  return response.data
}

export const createDevelopmentGoal = async (
  evaluationId: string,
  goal: Partial<DevelopmentGoal>,
): Promise<DevelopmentGoal> => {
  const response = await api.post(`/evaluations/${evaluationId}/development-goals/`, goal)
  return response.data
}

export const updateDevelopmentGoal = async (
  evaluationId: string,
  goal: Partial<DevelopmentGoal> & { id: string },
): Promise<DevelopmentGoal> => {
  const response = await api.patch(`/evaluations/${evaluationId}/development-goals/`, goal)
  return response.data
}

export const deleteDevelopmentGoal = async (evaluationId: string, goalId: string): Promise<void> => {
  await api.delete(`/evaluations/${evaluationId}/development-goals/`, { data: { id: goalId } })
}
